/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useState, useEffect } from "react";
import { AlertColor, Box, Button, Paper, Stack } from "@mui/material";
import { Notify } from "../../../components/Notify";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MaintenanceTemplateForm, { MaintenanceTemplateFormData } from "./MaintenanceTemplateForm";
import { YesNoCancelPopup } from "../../../components/YesNoCancelPopup";
import { IMaintenanceFile, useMaintenanceService } from "../../../services/registry/MaintenanceService";

interface HeadCell {
  id: string;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "maintenanceTemplate",
    label: "Maintenance Template Name",
    padding: "none",
  },
  {
    id: "actions",
    label: "Actions",
    align: "left",
  },
];

const MaintenanceTemplatesTableHead = () => {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.align} padding={headCell.padding}>
            <TableSortLabel>{headCell.label}</TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const MaintenanceTemplatesTableToolbar = ({
  onCreateClick,
  onRefreshClick,
}: {
  onCreateClick: () => void;
  onRefreshClick: () => void;
}) => {
  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
      <Stack spacing={1} direction="row" justifyContent="flex-end">
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={onRefreshClick}>
          Refresh
        </Button>
        <Button size="small" variant="contained" color="primary" startIcon={<AddIcon />} onClick={onCreateClick}>
          Create a new Template
        </Button>
      </Stack>
    </Toolbar>
  );
};

const MaintenanceTemplatesTable = ({ onApplyTemplate }: { onApplyTemplate: (templateName: string) => Promise<void> }) => {
  const maintenanceService = useMaintenanceService();

  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [maintenanceTemplates, setMaintenanceTemplates] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined);
  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formData, setFormData] = useState<MaintenanceTemplateFormData | undefined>(undefined);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | undefined>(undefined);

  useEffect(() => {
    (async () => fetchMaintenanceTemplates())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMaintenanceTemplates = async () => {
    try {
      setLoading(true);
      const { list } = await maintenanceService.listMaintenanceTemplates();
      setMaintenanceTemplates(list);
      setLoading(false);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to get templates.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanteTemplate = async (name: string) => {
    try {
      setLoading(true);
      const template = await maintenanceService.getMaintenanceTemplate(name);
      setLoading(false);
      return template;
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to get template.");
      setAlertSeverity("error");
    }
  };

  const handleCreateClick = () => {
    setFormMode("create");
    setFormData(undefined);
    setFormVisible(true);
  };

  const handleUpdateClick = async (templateName: string) => {
    setFormMode("edit");
    // In a real scenario, you'd fetch the template data here
    // For now, we'll just pass the name
    const maintenanceTemplate = (await fetchMaintenanteTemplate(templateName)) as IMaintenanceFile;
    setFormData({
      name: templateName,
      defaultMessage: maintenanceTemplate.defaultMessage || "",
      localizedMessages: maintenanceTemplate.localizedMessages || {},
    });
    setFormVisible(true);
  };

  const handleFormAccept = async (data: MaintenanceTemplateFormData) => {
    try {
      setLoading(true);
      if (formMode === "create") {
        await maintenanceService.createMaintenanceTemplate(data.name, data.defaultMessage, data.localizedMessages);
        setAlertMessage("Template created successfully.");
        setAlertSeverity("success");
      } else {
        await maintenanceService.updateMaintenanceTemplate(data.name, data.defaultMessage, data.localizedMessages);
        setAlertMessage("Template updated successfully.");
        setAlertSeverity("success");
      }
      await fetchMaintenanceTemplates();
      setFormVisible(false);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Failed to ${formMode} template.`);
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormDismiss = () => {
    setFormVisible(false);
    setFormData(undefined);
  };

  const handleRemoveClick = async (templateName: string) => {
    setTemplateToDelete(templateName);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      setLoading(true);
      setDeletePopupVisible(false);
      await maintenanceService.deleteMaintenanceTemplate(templateToDelete);
      setAlertMessage("Template deleted successfully.");
      setAlertSeverity("success");
      await fetchMaintenanceTemplates();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to delete template.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
      setTemplateToDelete(undefined);
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <MaintenanceTemplatesTableToolbar onCreateClick={handleCreateClick} onRefreshClick={fetchMaintenanceTemplates} />
            <Table sx={{ minWidth: 750 }} size="small">
              <MaintenanceTemplatesTableHead />
              <TableBody>
                {maintenanceTemplates.map((template, index) => {
                  const labelId = `maintenance-template-${index}`;
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      onClick={() => setSelectedItem(template)}
                      tabIndex={-1}
                      key={template}
                      selected={selectedItem === template}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="normal" />
                      <TableCell component="th" id={labelId} scope="row" padding="none">
                        {template}
                      </TableCell>
                      <TableCell align="left">
                        <Stack spacing={1} direction="row">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onApplyTemplate(template);
                            }}
                          >
                            Apply
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateClick(template);
                            }}
                          >
                            Update
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveClick(template);
                            }}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <MaintenanceTemplateForm
        visible={formVisible}
        mode={formMode}
        initialData={formData}
        onAccept={handleFormAccept}
        onDismiss={handleFormDismiss}
      />
      <YesNoCancelPopup
        visible={deletePopupVisible}
        title="Delete Template"
        message={`Are you sure you want to delete the template <b>"${templateToDelete}"</b>?<br/>This action cannot be undone.`}
        onYes={handleDeleteConfirm}
        onNo={() => {
          setDeletePopupVisible(false);
          setTemplateToDelete(undefined);
        }}
      />
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
      {loading && <LoadingOverlay />}
    </>
  );
};

export default MaintenanceTemplatesTable;
